import React, { useMemo, useRef, useState } from 'react';

import { useFormikContext } from 'formik';

import { cn, iaRa } from 'lib/helpers/utility';

import { withErrorBoundary } from 'components/shared/Error/Boundary';
import SectionList from 'components/practice/UserPreferences/components/SectionList';
import {
  DEFAULT_END,
  DEFAULT_START,
  DayCard,
  activeStyle,
  inactiveStyle
} from 'components/shared/AvailabilityTime';
import { availableTimes as defaultAvailableTimes } from 'components/practice/Settings/components/Calendar/components/AvailableTime/lib';

import { isDayAvailable } from '../lib/workingHoursUtils';

const PracticeWorkingDays = () => {
  const { values, setFieldValue } = useFormikContext();
  const [expandedDay, setExpandedDay] = useState(null);
  // Toggling the last active type off clears the day's hours, which also drops its
  // break. Hours come back from defaults on toggle-on; the break has nowhere to come
  // back from unless it is stashed here.
  const stashedBreaksRef = useRef({});

  const availableTimes = useMemo(
    () => values?.availableTimes || defaultAvailableTimes,
    [values?.availableTimes]
  );

  const updateAvailableTimes = (updater) => {
    const next = updater(availableTimes);
    setFieldValue('availableTimes', next);
  };

  const handleToggleType = (dayName, typeKey, enabled) => {
    updateAvailableTimes((prev) =>
      iaRa(prev).map((day) => {
        if (day.day !== dayName) return day;

        const updated = {
          ...day,
          [typeKey]: { ...(day[typeKey] || {}) }
        };

        if (enabled) {
          const hasTimes = updated[typeKey].starts_at != null && updated[typeKey].ends_at != null;
          if (!hasTimes) {
            updated[typeKey].starts_at = DEFAULT_START;
            updated[typeKey].ends_at = DEFAULT_END;
          }

          const stashedBreak = stashedBreaksRef.current[dayName];
          if (stashedBreak && !updated.break) {
            updated.break = stashedBreak;
            delete stashedBreaksRef.current[dayName];
          }
        } else {
          updated[typeKey].starts_at = null;
          updated[typeKey].ends_at = null;
        }

        if (!isDayAvailable(updated)) {
          if (updated.break) stashedBreaksRef.current[dayName] = updated.break;
          delete updated.break;
        }

        return updated;
      })
    );

    // When toggling virtual ON, expand the day
    if (typeKey === 'virtual' && enabled) {
      setExpandedDay(dayName);
    }
  };

  const handleDayClick = (dayName) => {
    const dayData = availableTimes.find((d) => d.day === dayName);
    const isAvailable = isDayAvailable(dayData);

    if (isAvailable) {
      // Day is active (blue) - remove all working hours
      updateAvailableTimes((prev) =>
        prev.map((day) => {
          if (day.day !== dayName) return day;
          const updated = {
            ...day,
            location: { starts_at: null, ends_at: null },
            virtual: { starts_at: null, ends_at: null }
          };
          delete updated.break;
          return updated;
        })
      );
      // Collapse if this day was expanded
      if (expandedDay === dayName) {
        setExpandedDay(null);
      }
    } else {
      // Day is inactive - add default 9-5 office hours and expand
      updateAvailableTimes((prev) =>
        prev.map((day) => {
          if (day.day !== dayName) return day;
          return {
            ...day,
            location: {
              ...(day.location || {}),
              starts_at: DEFAULT_START,
              ends_at: DEFAULT_END
            }
          };
        })
      );
      setExpandedDay(dayName);
    }
  };

  const availableWeekDays = iaRa(availableTimes).map((day) => ({
    day: day.day,
    available: isDayAvailable(day)
  }));

  const availableTimesRender = iaRa(availableTimes).map((day) => ({
    ...day,
    available: isDayAvailable(day)
  }));

  return (
    <SectionList
      title="Practice working days"
      description="Set your practice's weekly schedule, define office or virtual hours, and customize working times and breaks by day.">
      <div className="flex flex-col gap-3">
        <div className="flex gap-3">
          {availableWeekDays.map((day) => (
            <div
              key={day.day}
              onClick={() => handleDayClick(day.day)}
              className={cn(
                'flex h-[40px] w-[48px] cursor-pointer items-center justify-center rounded-md border text-sm font-500 shadow-sm',
                day.available ? activeStyle : inactiveStyle
              )}>
              {day.day.slice(0, 3)}
            </div>
          ))}
        </div>
        {availableTimesRender.map((day) => (
          <DayCard
            key={day.day}
            day={day}
            expandedDay={expandedDay}
            setExpandedDay={setExpandedDay}
            handleToggleType={handleToggleType}
            updateAvailableTimes={updateAvailableTimes}
          />
        ))}
      </div>
    </SectionList>
  );
};

export default withErrorBoundary(PracticeWorkingDays);
